import React, { useEffect, useState, useCallback, useRef } from "react";
import { getFiNumber, setFiExpensesOverride, clearFiExpensesOverride, setWithdrawalRate } from "../../api/savingsClient";
import { getNetWorth } from "../../api/plaidClient";
import { formatCurrencyCompact, formatPercent } from "../../utils/formatters";
import ChartLoader from "../ui/ChartLoader";

export default function FiNumberCard({ months = 6, syncing = false }) {
    const [data, setData] = useState(null);
    const [netWorth, setNetWorth] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showSettings, setShowSettings] = useState(false);
    const [expensesInput, setExpensesInput] = useState("");
    const [saving, setSaving] = useState(false);
    const [rateInput, setRateInput] = useState("4.0");
    const [settingsSaving, setSettingsSaving] = useState(false);
    const abortRef = useRef(null);

    const fetchData = useCallback(async () => {
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();

        setLoading(true);
        setError(null);

        try {
            const [fiResp, nwResp] = await Promise.all([
                getFiNumber(undefined, months, abortRef.current.signal),
                getNetWorth().catch(() => null),
            ]);
            setData(fiResp);
            setNetWorth(nwResp?.netWorth || 0);
            setRateInput(String(parseFloat(fiResp.withdrawalRate)));
            if (fiResp.expensesOverridden) {
                setExpensesInput(String(Math.round(fiResp.annualExpenses)));
            }
        } catch (e) {
            if (e.name === "AbortError" || e.name === "CanceledError") return;
            setError("Failed to load FI data");
        } finally {
            setLoading(false);
        }
    }, [months]);

    useEffect(() => {
        fetchData();
        return () => abortRef.current?.abort();
    }, [fetchData]);

    const handleClearOverride = async () => {
        setSaving(true);
        try {
            await clearFiExpensesOverride();
            setExpensesInput("");
            await fetchData();
        } catch {
            setError("Failed to clear override");
        } finally {
            setSaving(false);
        }
    };

    const handleSaveSettings = useCallback(async () => {
        setSettingsSaving(true);
        try {
            const rateValue = parseFloat(rateInput);
            if (!isNaN(rateValue)) {
                const clamped = Math.min(Math.max(rateValue, 1.0), 10.0);
                setRateInput(String(clamped));
                await setWithdrawalRate(clamped);
            }

            const expValue = parseFloat(expensesInput);
            if (!isNaN(expValue) && expValue > 0) {
                await setFiExpensesOverride(expValue);
            }

            await fetchData();
            setShowSettings(false);
        } catch {
            setError("Failed to save settings");
        } finally {
            setSettingsSaving(false);
        }
    }, [rateInput, expensesInput, fetchData]);

    if (loading) {
        return (
            <div className="card p-6">
                <div className="h-5 bg-gray-100 rounded w-44 mb-4 animate-pulse" />
                <ChartLoader height={64} />
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="card p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">FI number</h3>
                {!error && syncing ? (
                    <div className="flex items-center gap-3 py-4">
                        <div className="animate-spin w-5 h-5 border-2 border-gray-200 border-t-accent-500 rounded-full" />
                        <p className="text-gray-500 text-sm">Syncing your financial data...</p>
                    </div>
                ) : (
                    <p className="text-gray-500 text-sm">{error || "No data available"}</p>
                )}
            </div>
        );
    }

    const fiNumber = data.fiNumber;
    const progress = fiNumber > 0 ? Math.min((netWorth / fiNumber) * 100, 100) : 0;
    const remaining = Math.max(fiNumber - netWorth, 0);

    const annualSavings = data.annualSavings;
    let yearsToFi = null;
    if (annualSavings > 0 && remaining > 0) {
        const realReturn = 0.07;
        let balance = netWorth;
        let years = 0;
        while (balance < fiNumber && years < 100) {
            balance = balance * (1 + realReturn) + annualSavings;
            years++;
        }
        if (years < 100) yearsToFi = years;
    }

    return (
        <div className="card p-6">
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900">FI number</h3>
                <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">{formatPercent(data.withdrawalRate)} withdrawal rate</span>
                    <button
                        onClick={() => setShowSettings(!showSettings)}
                        className="text-gray-400 hover:text-gray-700 transition-colors"
                        title="Configure FI settings"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                        </svg>
                    </button>
                </div>
            </div>

            {showSettings && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
                    <div>
                        <label className="text-xs text-gray-600 block mb-1">Withdrawal rate (%)</label>
                        <div className="flex gap-2">
                            <div className="relative flex-1">
                                <input
                                    type="number"
                                    value={rateInput}
                                    onChange={(e) => setRateInput(e.target.value)}
                                    min="1"
                                    max="10"
                                    step="0.1"
                                    placeholder="4.0"
                                    className="w-full bg-white border border-gray-200 rounded px-2 py-1.5 text-sm text-gray-900 placeholder-gray-400 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20"
                                />
                                <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="text-xs text-gray-600 block mb-1">
                            Annual expenses override
                            <span className="text-gray-400 ml-1">(blank = auto)</span>
                        </label>
                        <div className="flex gap-2">
                            <div className="relative flex-1">
                                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                                <input
                                    type="number"
                                    value={expensesInput}
                                    onChange={(e) => setExpensesInput(e.target.value)}
                                    placeholder={String(Math.round(data.calculatedAnnualExpenses))}
                                    className="w-full bg-white border border-gray-200 rounded px-2 py-1.5 pl-6 text-sm text-gray-900 placeholder-gray-400 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20"
                                />
                            </div>
                            {data.expensesOverridden && (
                                <button
                                    onClick={handleClearOverride}
                                    disabled={saving}
                                    className="px-3 py-1.5 bg-white border border-gray-200 hover:bg-gray-50 disabled:text-gray-400 text-gray-700 text-xs rounded transition-colors"
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                    </div>

                    <button
                        onClick={handleSaveSettings}
                        disabled={settingsSaving}
                        className="w-full px-3 py-1.5 bg-accent-500 hover:bg-accent-600 disabled:bg-gray-200 disabled:text-gray-400 text-white text-sm font-medium rounded transition-colors"
                    >
                        {settingsSaving ? "Saving..." : "Save"}
                    </button>
                </div>
            )}

            <div className="text-3xl font-semibold text-gray-900 mb-1 tracking-tight">
                {formatCurrencyCompact(fiNumber)}
            </div>
            <p className="text-sm text-gray-500 mb-4">
                Based on {formatCurrencyCompact(data.annualExpenses)}/yr in expenses
                {data.expensesOverridden ? (
                    <span className="text-gray-700 ml-1">· manual</span>
                ) : (
                    <span className="text-gray-400 ml-1">· auto</span>
                )}
            </p>

            <div className="mb-3">
                <div className="flex justify-between text-xs text-gray-600 mb-1.5">
                    <span>{formatPercent(progress)} there</span>
                    <span>{formatCurrencyCompact(remaining)} to go</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                        className="h-full bg-accent-500 rounded-full transition-all duration-500"
                        style={{ width: `${progress}%` }}
                    />
                </div>
            </div>

            {yearsToFi !== null && (
                <p className="text-sm text-gray-700">
                    At your current savings rate, you could reach FI in{" "}
                    <span className="text-accent-600 font-semibold">
                        ~{yearsToFi} {yearsToFi === 1 ? "year" : "years"}
                    </span>
                </p>
            )}

            {annualSavings <= 0 && (
                <p className="text-sm text-danger-500">
                    Your expenses currently exceed your income. Focus on increasing your savings rate.
                </p>
            )}
        </div>
    );
}
